/**
 * RapprochementUnifiePage - Page unifiée Bank + Wave (Phase 4 - Fév 2026)
 * Basée sur la maquette CFO Cockpit fournie par l'utilisateur.
 *
 * Structure :
 *   Header : période · sélecteur source Bank/Wave · sélecteur compte · actions
 *   6 KPI cards CFO (communes) + 2 cards contextuelles selon source
 *   Zone import OCR (workflow 5 étapes)
 *   Tableau transactions central (colonnes uniformes)
 *   Panneau détail droit : Détail tx + 3 suggestions (Op / Contrat / Facture)
 *   Synthèse de clôture (statut mois, verrouillage, écart)
 *
 * Reuse : endpoints Bank + Wave existants (aucun nouveau backend requis).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import SavedViewsMenu from '../../components/common/SavedViewsMenu';
import {
  Landmark, Wallet, RefreshCw, Upload, Sparkles, Lock, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, FileText, Search, Link2, Ban, Edit3, Loader2,
  ArrowUpRight, ArrowDownRight, Building2, X, ScrollText, Calendar,
  ChevronLeft, ChevronRight, Trash2, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

/* ═══════════════════════════════════════════════════════════════════════
   HELPERS UI PARTAGÉS
   ═══════════════════════════════════════════════════════════════════════ */

const KpiCard = ({ icon: Icon, label, value, unit = 'XOF', accent = 'blue', subline, testId }) => {
  const themes = {
    blue: 'from-blue-50 to-white border-blue-200 text-blue-600',
    green: 'from-emerald-50 to-white border-emerald-200 text-emerald-600',
    orange: 'from-amber-50 to-white border-amber-200 text-amber-600',
    red: 'from-rose-50 to-white border-rose-200 text-rose-600',
    purple: 'from-violet-50 to-white border-violet-200 text-violet-600',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-600',
  };
  return (
    <div
      className={`rounded-xl border bg-gradient-to-b ${themes[accent] || themes.blue} p-4 flex flex-col gap-2`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-lg lg:text-xl font-semibold text-zinc-900">
        {typeof value === 'number' ? fmt(value) : value}
        {unit ? <span className="text-xs font-normal text-zinc-500 ml-1">{unit}</span> : null}
      </div>
      {subline ? <div className="text-xs text-zinc-500">{subline}</div> : null}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  // Mapping unifié Bank + Wave sur 6 couleurs sémantiques
  const map = {
    // Bank
    reconciled: { color: 'bg-emerald-100 text-emerald-700 ring-emerald-200', label: 'Rapprochée' },
    auto_reconciled: { color: 'bg-emerald-100 text-emerald-700 ring-emerald-200', label: 'Rapprochée (auto)' },
    unreconciled: { color: 'bg-rose-100 text-rose-700 ring-rose-200', label: 'À rapprocher' },
    partially_reconciled: { color: 'bg-amber-100 text-amber-700 ring-amber-200', label: 'Partiellement rapprochée' },
    ignored: { color: 'bg-zinc-100 text-zinc-600 ring-zinc-200', label: 'Ignorée' },
    partially_matched: { color: 'bg-amber-100 text-amber-700 ring-amber-200', label: 'Partielle' },
    fee: { color: 'bg-zinc-100 text-zinc-600 ring-zinc-200', label: 'Frais bancaire' },
    reversed: { color: 'bg-violet-100 text-violet-700 ring-violet-200', label: 'Reversée' },
    closed: { color: 'bg-blue-100 text-blue-700 ring-blue-200', label: 'Clôturée' },
    needs_review: { color: 'bg-amber-100 text-amber-700 ring-amber-200', label: 'À vérifier' },
    discrepancy: { color: 'bg-rose-100 text-rose-700 ring-rose-200', label: 'Écart' },
    // Wave
    rapprochee_auto: { color: 'bg-emerald-100 text-emerald-700 ring-emerald-200', label: 'Rapprochée (auto)' },
    rapprochee_manuelle: { color: 'bg-emerald-100 text-emerald-700 ring-emerald-200', label: 'Rapprochée' },
    rapprochement_partiel: { color: 'bg-amber-100 text-amber-700 ring-amber-200', label: 'Partielle' },
    paiement_groupe: { color: 'bg-blue-100 text-blue-700 ring-blue-200', label: 'Paiement groupé' },
    ecart_detecte: { color: 'bg-rose-100 text-rose-700 ring-rose-200', label: 'Écart' },
    a_verifier: { color: 'bg-amber-100 text-amber-700 ring-amber-200', label: 'À vérifier' },
    non_rapprochee: { color: 'bg-rose-100 text-rose-700 ring-rose-200', label: 'À rapprocher' },
    non_concernee_wave: { color: 'bg-zinc-100 text-zinc-600 ring-zinc-200', label: 'Hors périmètre' },
    payee_hors_wave: { color: 'bg-zinc-100 text-zinc-600 ring-zinc-200', label: 'Hors Wave' },
    annulee_reversee: { color: 'bg-violet-100 text-violet-700 ring-violet-200', label: 'Reversée' },
  };
  const s = map[status] || { color: 'bg-slate-100 text-slate-700 ring-slate-200', label: status || 'N/A' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${s.color}`}>
      {s.label}
    </span>
  );
};

/* ─── Sélecteur période lisible (ex: "Juillet 2026") avec état clôture ─── */
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const readablePeriod = (yyyy_mm) => {
  if (!yyyy_mm) return '';
  const [y, m] = yyyy_mm.split('-');
  return `${MONTHS_FR[+m - 1] || m} ${y}`;
};
const periodRange = (yyyy_mm) => {
  if (!yyyy_mm) return '';
  const [y, m] = yyyy_mm.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const p = (n) => String(n).padStart(2, '0');
  return `01/${p(m)}/${y} → ${last}/${p(m)}/${y}`;
};

/* ═══════════════════════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL
   ═══════════════════════════════════════════════════════════════════════ */

const nowPeriod = () => new Date().toISOString().slice(0, 7);

export default function RapprochementUnifiePage() {
  const [source, setSource] = useState('bank'); // 'bank' | 'wave'
  const [period, setPeriod] = useState(nowPeriod());
  const [loading, setLoading] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [kpis, setKpis] = useState({});
  const [reconKpis, setReconKpis] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [closureStatus, setClosureStatus] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [ocrPanelOpen, setOcrPanelOpen] = useState(false);
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrUploading, setOcrUploading] = useState(false);
  const [ocrStep, setOcrStep] = useState(0);  // 0..4 (upload → OCR → preview → contrôle solde → import validé)
  const [ocrShowAccountPicker, setOcrShowAccountPicker] = useState(false);
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);

  // Pagination + sélection multiple + actions groupées (Fév 2026)
  const [page, setPage] = useState(1);          // 1-indexed
  const [pageSize, setPageSize] = useState(25); // 25 par défaut (modifiable)
  const [totalTx, setTotalTx] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  /* ─────── Data fetching ─────── */
  const fetchAccounts = useCallback(async () => {
    try {
      if (source === 'bank') {
        const { data } = await axios.get(`${API}/bank/accounts`);
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
        setAccounts(list);
        if (list.length && !selectedAccountId) {
          setSelectedAccountId(list[0].id);
        }
      } else {
        // Wave = un unique wallet
        setAccounts([{ id: 'wave-wallet', name: 'Wave Business', balance: null, currency: 'XOF' }]);
        setSelectedAccountId('wave-wallet');
      }
    } catch (e) {
      setAccounts([]);
      toast({ title: 'Erreur chargement comptes', variant: 'destructive' });
    }
  }, [source, selectedAccountId]);

  const fetchKpis = useCallback(async () => {
    try {
      if (source === 'bank' && selectedAccountId) {
        const [cfoR, reconR] = await Promise.all([
          axios.get(`${API}/bank/cfo-kpis/${selectedAccountId}`, { params: { yyyy_mm: period } }),
          axios.get(`${API}/bank/accounts/${selectedAccountId}/reconciliation-kpis`),
        ]);
        setKpis(cfoR.data || {});
        setReconKpis(reconR.data || {});
      } else if (source === 'wave') {
        const [cfoR, reconR] = await Promise.all([
          axios.get(`${API}/wave/cfo-kpis`, { params: { period } }),
          axios.get(`${API}/wave/reconciliation-kpis`, { params: { period } }),
        ]);
        setKpis(cfoR.data || {});
        setReconKpis(reconR.data || {});
      }
    } catch (e) {
      // silent — KPIs ne bloquent pas la page
    }
  }, [source, selectedAccountId, period]);

  const fetchTransactions = useCallback(async () => {
    try {
      if (source === 'bank' && selectedAccountId) {
        const { data } = await axios.get(`${API}/bank/transactions`, {
          params: { account_id: selectedAccountId, limit: pageSize, skip: (page - 1) * pageSize },
        });
        const items = data?.items || data || [];
        setTransactions(items);
        setTotalTx(data?.total ?? items.length);
      } else if (source === 'wave') {
        const { data } = await axios.get(`${API}/wave/transactions`, {
          params: { period, limit: pageSize, skip: (page - 1) * pageSize },
        });
        const items = data?.items || data || [];
        setTransactions(items);
        setTotalTx(data?.total ?? items.length);
      }
    } catch (e) {
      toast({ title: 'Erreur chargement transactions', variant: 'destructive' });
    }
  }, [source, selectedAccountId, period, page, pageSize]);

  const fetchClosureStatus = useCallback(async () => {
    try {
      if (source === 'bank' && selectedAccountId) {
        const { data } = await axios.get(
          `${API}/bank/periods/${selectedAccountId}/${period}`,
        );
        setClosureStatus(data);
      } else if (source === 'wave') {
        const { data } = await axios.get(`${API}/wave/months/${period}/status`);
        setClosureStatus(data);
      }
    } catch (e) {
      setClosureStatus(null);
    }
  }, [source, selectedAccountId, period]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([fetchKpis(), fetchTransactions(), fetchClosureStatus()]);
    setLoading(false);
  }, [fetchKpis, fetchTransactions, fetchClosureStatus]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  // fetchAll pour Bank : nécessite selectedAccountId
  //         pour Wave : pas de compte à sélectionner, on charge directement
  useEffect(() => {
    if (source === 'wave' || selectedAccountId) fetchAll();
  }, [fetchAll, source, selectedAccountId]);
  // Reset page + sélection lorsque source/compte/période change
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [source, selectedAccountId, period, pageSize]);

  const totalPages = Math.max(1, Math.ceil((totalTx || 0) / pageSize));
  const allOnPageSelected = transactions.length > 0 && transactions.every(t => selectedIds.has(t.id));
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allOnPageSelected) transactions.forEach(t => next.delete(t.id));
      else transactions.forEach(t => next.add(t.id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    const isBank = source === 'bank';
    setBulkBusy(true);
    try {
      if (action === 'reconcile') {
        const url = isBank ? `${API}/bank/reconcile/bulk` : `${API}/wave/reconcile/bulk`;
        const body = isBank
          ? { account_id: selectedAccountId, tx_ids: ids }
          : { transactionIds: ids };
        await axios.post(url, body);
        toast({ title: 'Rapprochement bulk lancé', description: `${ids.length} tx traitée(s)` });
      } else if (action === 'ignore') {
        const url = isBank ? `${API}/bank/transactions/bulk-ignore` : `${API}/wave/transactions/bulk-ignore`;
        const body = isBank ? { tx_ids: ids } : { transactionIds: ids, reason: 'hors_perimetre' };
        const { data } = await axios.post(url, body);
        toast({ title: 'Ignorées en masse', description: `${data?.updated ?? ids.length} tx ignorée(s)` });
      } else if (action === 'delete') {
        if (!window.confirm(`Supprimer définitivement ${ids.length} transaction(s) ? Cette action est irréversible.`)) {
          setBulkBusy(false); return;
        }
        const url = isBank ? `${API}/bank/transactions/bulk-delete` : `${API}/wave/transactions/bulk-delete`;
        const body = isBank ? { tx_ids: ids } : { transactionIds: ids };
        const { data } = await axios.post(url, body);
        toast({ title: 'Suppression en masse', description: `${data?.deleted ?? ids.length} tx supprimée(s)` });
      } else if (action === 'export') {
        const rows = transactions.filter(t => selectedIds.has(t.id));
        const headers = ['Date', 'Libellé', 'Référence', 'Type', 'Débit', 'Crédit', 'Statut'];
        const csv = [
          headers.join(';'),
          ...rows.map(tx => {
            const isDebit = tx.direction === 'debit' || tx.amount < 0;
            const d = tx.operation_date || tx.transactionDate || tx.date;
            return [
              d ? new Date(d).toLocaleDateString('fr-FR') : '',
              (tx.label || tx.waveRef || tx.description || '').replace(/;/g, ','),
              tx.reference || tx.waveRef || '',
              tx.type || (isDebit ? 'Débit' : 'Crédit'),
              isDebit ? Math.abs(tx.amount) : '',
              !isDebit ? Math.abs(tx.amount) : '',
              tx.reconciliation_status || tx.matchStatus || '',
            ].join(';');
          }),
        ].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rapprochement_${source}_${period}_selection_${rows.length}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({ title: 'Export CSV', description: `${rows.length} ligne(s) exportée(s)` });
        setBulkBusy(false);
        return;
      }
      clearSelection();
      await fetchAll();
    } catch (e) {
      toast({
        title: 'Erreur action groupée',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setBulkBusy(false);
    }
  };

  /* ─────── Sélection transaction → candidates ─────── */
  const selectTx = async (tx) => {
    setSelectedTx(tx);
    setCandidates([]);
    if (!tx?.id) return;
    setCandidatesLoading(true);
    try {
      const url = source === 'bank'
        ? `${API}/bank/transactions/${tx.id}/candidates`
        : `${API}/wave/transactions/${tx.id}/candidates`;
      const { data } = await axios.get(url);
      setCandidates(data?.candidates || []);
    } catch (e) {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const runAutoMatch = async () => {
    try {
      if (source === 'bank') {
        await axios.post(`${API}/bank/accounts/${selectedAccountId}/auto-match`, {
          use_llm: true, threshold_auto: 0.88, threshold_suggest: 0.65,
        });
      } else {
        await axios.post(`${API}/wave/reconcile/auto`, { period });
      }
      toast({ title: 'Rapprochement automatique lancé' });
      fetchAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  const ignoreTx = async (tx) => {
    try {
      const url = source === 'bank'
        ? `${API}/bank/transactions/${tx.id}/ignore`
        : `${API}/wave/transactions/${tx.id}/ignore`;
      await axios.post(url, source === 'wave' ? { reason: 'hors_perimetre' } : {});
      toast({ title: 'Transaction ignorée' });
      fetchTransactions();
      if (selectedTx?.id === tx.id) setSelectedTx(null);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  const matchTx = async (tx, candidate) => {
    try {
      if (source === 'bank') {
        await axios.post(`${API}/bank/transactions/${tx.id}/match`, {
          source: candidate.source, source_id: candidate.id,
        });
      } else {
        await axios.post(`${API}/wave/transactions/${tx.id}/match`, {
          allocations: [{
            operationId: candidate.id,
            allocatedAmount: candidate.amount || tx.amount,
            kind: 'total',
          }],
        });
      }
      toast({ title: 'Rapprochement effectué' });
      fetchAll();
      setSelectedTx(null);
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  const generateReport = async () => {
    try {
      if (source === 'bank') {
        window.open(`${API}/bank/accounts/${selectedAccountId}/report?yyyy_mm=${period}`, '_blank');
      } else {
        await axios.post(`${API}/wave/report`, { period });
        toast({ title: 'Rapport Wave généré', description: `Période ${period}` });
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    }
  };

  const closureBlocked = closureStatus?.locked || closureStatus?.state === 'locked' || closureStatus?.status === 'closed';

  /* ─────── KPIs affichés ─────── */
  const kpisDisplay = useMemo(() => {
    const base = [
      { icon: Landmark, label: 'Solde / Trésorerie', value: kpis.balance || kpis.consolidated_treasury || kpis.wave_wallet_balance || 0, accent: 'blue' },
      { icon: ArrowUpRight, label: 'Total crédits', value: kpis.total_credits || 0, accent: 'green' },
      { icon: ArrowDownRight, label: 'Total débits', value: kpis.total_debits || 0, accent: 'red' },
      { icon: CheckCircle2, label: 'Taux rapprochement', value: `${(kpis.reconciliation_rate ?? reconKpis.reconciled_pct ?? 0)}`, unit: '%', accent: 'green', subline: `${kpis.matched_count ?? reconKpis.reconciled ?? 0} / ${(kpis.matched_count ?? reconKpis.reconciled ?? 0) + (kpis.unmatched_count ?? reconKpis.unreconciled ?? 0)} tx` },
      { icon: AlertTriangle, label: 'Écart / à vérifier', value: kpis.discrepancy_count ?? reconKpis.discrepancy ?? 0, unit: 'tx', accent: 'orange', subline: `${fmt(kpis.discrepancy_total || 0)} XOF` },
      { icon: FileText, label: 'Frais bancaires / Wave', value: kpis.total_fees || 0, accent: 'slate' },
    ];
    if (source === 'wave') {
      base.push({
        icon: Wallet, label: 'Paiements chauffeurs',
        value: kpis.total_driver_payouts || 0, accent: 'purple',
      });
      base.push({
        icon: Search, label: 'Non identifiées',
        value: kpis.unidentified_count || 0, unit: 'tx', accent: 'red',
      });
    } else {
      base.push({
        icon: Building2, label: 'Solde comptes actifs',
        value: kpis.consolidated_treasury || kpis.balance || 0, accent: 'blue',
        subline: `${accounts.length || 0} compte${(accounts.length || 0) > 1 ? 's' : ''}`,
      });
      base.push({
        icon: AlertTriangle, label: 'Exceptions',
        value: reconKpis.needs_review ?? kpis.needs_review_count ?? 0, unit: 'tx', accent: 'orange',
      });
    }
    return base;
  }, [kpis, reconKpis, source, accounts]);

  /* ═════════════════════════════════════════════════════════════════════ */

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ───────── HEADER ───────── */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
              <Landmark className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900" data-testid="rapprochement-title">
                Rapprochement {source === 'bank' ? 'bancaire' : 'Wave'}
              </h1>
              <p className="text-xs text-slate-500">
                {source === 'bank' ? 'Comptes bancaires · OCR relevés' : 'Wave Business · encaissements & paiements'} · opérations, contrats, factures contrat
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Sélecteur source Bank ⇄ Wave */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="source-toggle">
              <button
                onClick={() => setSource('bank')}
                className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${source === 'bank' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="source-bank-btn"
              >
                <Landmark className="h-4 w-4" /> Banque
              </button>
              <button
                onClick={() => setSource('wave')}
                className={`px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 ${source === 'wave' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid="source-wave-btn"
              >
                <Wallet className="h-4 w-4" /> Wave
              </button>
            </div>

            {/* Période — bouton lisible "Juillet 2026" avec dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowPeriodPicker(v => !v)}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="period-btn"
                title={periodRange(period)}
              >
                <Calendar className="h-4 w-4 text-slate-500" />
                <span className="font-medium">{readablePeriod(period)}</span>
                {closureStatus?.status === 'closed' || closureStatus?.state === 'closed' || closureStatus?.locked ? (
                  <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full">
                    <Lock className="h-3 w-3" /> Clôturé
                  </span>
                ) : (
                  <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">En cours</span>
                )}
                <span className="text-slate-400 text-xs">▼</span>
              </button>
              {showPeriodPicker && (
                <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg p-3 z-30" data-testid="period-dropdown">
                  <div className="text-xs text-slate-500 mb-2">Période : <span className="font-medium text-slate-700">{periodRange(period)}</span></div>
                  <input
                    type="month"
                    value={period}
                    onChange={(e) => { setPeriod(e.target.value); }}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-2"
                    data-testid="period-input"
                  />
                  <div className="grid grid-cols-2 gap-1">
                    {[0, -1, -2, -3].map(offset => {
                      const d = new Date();
                      d.setMonth(d.getMonth() + offset);
                      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                      return (
                        <button
                          key={offset}
                          onClick={() => { setPeriod(val); setShowPeriodPicker(false); }}
                          className={`text-xs px-2 py-1 rounded ${period === val ? 'bg-blue-100 text-blue-700 font-medium' : 'hover:bg-slate-100 text-slate-600'}`}
                        >
                          {readablePeriod(val)}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setShowPeriodPicker(false)}
                    className="w-full mt-2 text-xs bg-blue-600 text-white py-1.5 rounded hover:bg-blue-700"
                  >
                    Valider
                  </button>
                </div>
              )}
            </div>

            {/* Actions */}
            <SavedViewsMenu
              scope="unified"
              currentFilters={{ source, period, account_id: selectedAccountId }}
              onLoad={(f) => {
                if (f?.source) setSource(f.source);
                if (f?.period) setPeriod(f.period);
                if (f?.account_id) setSelectedAccountId(f.account_id);
              }}
            />
            <button
              onClick={fetchAll}
              className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>

            {source === 'bank' && (
              <button
                onClick={() => setOcrPanelOpen(v => !v)}
                className="inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-blue-700"
                data-testid="import-ocr-btn"
              >
                <Upload className="h-4 w-4" />
                Importer relevé (OCR)
              </button>
            )}

            <button
              onClick={runAutoMatch}
              className="inline-flex items-center gap-1.5 border border-violet-300 text-violet-700 bg-violet-50 rounded-lg px-3 py-1.5 text-sm hover:bg-violet-100"
              data-testid="auto-match-btn"
            >
              <Sparkles className="h-4 w-4" />
              Rapprochement auto
            </button>

            <button
              disabled={closureBlocked}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${closureBlocked ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
              data-testid="close-month-btn"
              title={closureBlocked ? 'Écarts à traiter avant clôture' : 'Clôturer le mois'}
            >
              <Lock className="h-4 w-4" />
              Clôturer le mois
            </button>
          </div>
        </div>

        {/* Comptes (cartes horizontales) */}
        {source === 'bank' && accounts.length > 0 && (
          <div className="px-6 pb-4 flex gap-3 overflow-x-auto">
            {accounts.map(acc => (
              <button
                key={acc.id}
                onClick={() => setSelectedAccountId(acc.id)}
                className={`min-w-[220px] text-left rounded-xl border-2 p-3 transition ${selectedAccountId === acc.id ? 'border-blue-500 bg-blue-50/50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid={`account-card-${acc.id}`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Landmark className="h-4 w-4 text-blue-500" />
                  {acc.name}
                  {selectedAccountId === acc.id && (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">Sélectionné</span>
                  )}
                </div>
                <div className="text-lg font-semibold text-slate-900 mt-1">{fmt(acc.currentBalance || acc.balance || 0)} <span className="text-xs text-slate-500 font-normal">XOF</span></div>
                <div className="text-[11px] text-slate-500 mt-0.5">N° {acc.number || acc.accountNumber || acc.id.slice(0, 8)}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ───────── ZONE PRINCIPALE ───────── */}
      <div className="px-6 py-5 grid grid-cols-12 gap-4">
        {/* Left : KPIs + table */}
        <div className="col-span-12 xl:col-span-9 space-y-4">
          {/* KPI CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            {kpisDisplay.map((k, i) => (
              <KpiCard key={i} testId={`kpi-${i}`} {...k} />
            ))}
          </div>

          {/* OCR IMPORT PANEL (Phase E - Fév 2026) */}
          {ocrPanelOpen && source === 'bank' && (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="ocr-import-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Upload className="h-4 w-4 text-blue-500" />
                  Import de relevé bancaire (OCR)
                </div>
                <button onClick={() => { setOcrPanelOpen(false); setOcrFile(null); setOcrStep(0); }} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Contexte compte sélectionné — obligatoire avant OCR */}
              {(() => {
                const currentAcc = accounts.find(a => a.id === selectedAccountId);
                const isValid = currentAcc && (currentAcc.is_active !== false);
                return (
                  <div className={`mb-3 rounded-lg border p-3 ${isValid ? 'bg-slate-50 border-slate-200' : 'bg-rose-50 border-rose-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-xs space-y-0.5">
                        <div><span className="text-slate-500">Compte bancaire concerné :</span> <span className="font-semibold text-slate-800">{currentAcc?.bank_name || currentAcc?.name || '— non sélectionné —'}</span></div>
                        <div><span className="text-slate-500">N° compte :</span> <span className="font-mono">{currentAcc?.account_number || currentAcc?.number || '—'}</span></div>
                        <div><span className="text-slate-500">Période :</span> {readablePeriod(period)}</div>
                        {ocrFile && <div><span className="text-slate-500">Fichier :</span> <span className="font-mono text-[11px]">{ocrFile.name}</span></div>}
                      </div>
                      <button
                        onClick={() => setOcrShowAccountPicker(v => !v)}
                        className="text-xs bg-white border border-slate-300 px-2 py-1 rounded hover:bg-slate-50 whitespace-nowrap"
                        data-testid="ocr-change-account-btn"
                      >
                        Changer de compte
                      </button>
                    </div>
                    {ocrShowAccountPicker && (
                      <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-2">
                        {accounts.filter(a => a.is_active !== false).map(acc => (
                          <button
                            key={acc.id}
                            onClick={() => { setSelectedAccountId(acc.id); setOcrShowAccountPicker(false); }}
                            className={`text-left rounded border p-2 text-xs hover:border-blue-400 ${selectedAccountId === acc.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}
                            data-testid={`ocr-select-account-${acc.id}`}
                          >
                            <div className="font-semibold text-slate-800">{acc.bank_name || acc.name}</div>
                            <div className="text-slate-500 font-mono">{acc.account_number || acc.number}</div>
                          </button>
                        ))}
                        {accounts.filter(a => a.is_active !== false).length === 0 && (
                          <div className="col-span-2 text-xs text-rose-600 flex items-center gap-2 p-2">
                            <AlertTriangle className="h-4 w-4" />
                            Aucun compte bancaire actif — créez d&apos;abord un compte
                          </div>
                        )}
                      </div>
                    )}
                    {!isValid && (
                      <div className="mt-2 text-xs text-rose-700 bg-rose-100 rounded px-2 py-1 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Sélectionnez un compte bancaire actif avant de lancer l&apos;OCR.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="grid grid-cols-12 gap-4">
                {/* Drop zone */}
                <div className="col-span-12 md:col-span-5">
                  <label
                    htmlFor="ocr-file-input"
                    className={`block rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition ${ocrFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50'}`}
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { setOcrFile(f); setOcrStep(1); } }}
                    data-testid="ocr-dropzone"
                  >
                    <input
                      id="ocr-file-input"
                      type="file"
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) { setOcrFile(f); setOcrStep(1); } }}
                      className="hidden"
                    />
                    <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                    <div className="text-sm text-slate-700 font-medium">{ocrFile ? ocrFile.name : 'Glissez un relevé PDF/image ici'}</div>
                    <div className="text-xs text-slate-500 mt-1">{ocrFile ? `${Math.round(ocrFile.size / 1024)} KB` : 'PDF, JPG, PNG · Max 20 MB'}</div>
                  </label>
                  <button
                    disabled={!ocrFile || ocrUploading || !accounts.find(a => a.id === selectedAccountId && a.is_active !== false)}
                    onClick={async () => {
                      const currentAcc = accounts.find(a => a.id === selectedAccountId);
                      if (!currentAcc || currentAcc.is_active === false) {
                        toast({
                          title: 'Compte bancaire requis',
                          description: 'Sélectionnez un compte bancaire actif avant l\'OCR.',
                          variant: 'destructive',
                        });
                        setOcrShowAccountPicker(true);
                        return;
                      }
                      setOcrUploading(true);
                      const fd = new FormData();
                      fd.append('file', ocrFile);
                      fd.append('account_id', selectedAccountId);
                      try {
                        setOcrStep(2);
                        const { data } = await axios.post(`${API}/bank/statements/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
                        setOcrStep(3);
                        // Normalisation N° compte : supprime espaces + caractères spéciaux
                        const normalize = (s) => (s || '').replace(/[\s\-_.]/g, '').replace(/^0+/, '');
                        const detectedNumber = data?.detected_account_number || data?.account_number_detected;
                        const currentNumber = normalize(currentAcc?.account_number || currentAcc?.number || '');
                        const detectedClean = normalize(detectedNumber || '');
                        if (detectedClean && currentNumber && detectedClean !== currentNumber) {
                          toast({
                            title: '⚠️ Contrôle de cohérence',
                            description: `N° détecté par OCR (${detectedNumber}) ≠ compte sélectionné (${currentAcc.account_number || currentAcc.number}). Vérifiez avant import définitif.`,
                            variant: 'destructive',
                            duration: 10000,
                          });
                        } else {
                          toast({ title: 'OCR terminé', description: `${(data?.transactions?.length ?? data?.lines?.length ?? data?.count ?? 0)} lignes détectées.` });
                        }
                        setOcrStep(4);
                        setTimeout(() => { setOcrPanelOpen(false); setOcrFile(null); setOcrStep(0); fetchAll(); }, 2000);
                      } catch (e) {
                        const errDetail = e?.response?.data?.detail || String(e);
                        const isAccountErr = /introuvable|inactif|compte/i.test(errDetail);
                        toast({
                          title: 'Erreur OCR',
                          description: isAccountErr
                            ? `${errDetail} — Sélectionnez un compte actif ou créez le compte correspondant.`
                            : errDetail,
                          variant: 'destructive',
                          duration: 10000,
                        });
                        if (isAccountErr) setOcrShowAccountPicker(true);
                        setOcrStep(0);
                      } finally { setOcrUploading(false); }
                    }}
                    className="mt-3 w-full bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                    data-testid="ocr-upload-btn"
                    title={!selectedAccountId ? 'Sélectionnez d\'abord un compte bancaire' : (!ocrFile ? 'Choisissez un fichier' : '')}
                  >
                    {ocrUploading ? <><Loader2 className="h-4 w-4 mr-1 inline animate-spin" /> Analyse en cours…</> : 'Lancer l\'analyse OCR'}
                  </button>
                </div>
                {/* Étapes */}
                <div className="col-span-12 md:col-span-7">
                  <div className="text-xs font-semibold text-slate-600 mb-2">Progression</div>
                  <div className="space-y-2">
                    {[
                      { label: 'Upload du relevé', desc: 'Fichier PDF ou image' },
                      { label: 'OCR', desc: 'Extraction des données' },
                      { label: 'Prévisualisation', desc: 'Aperçu des transactions' },
                      { label: 'Contrôle solde', desc: 'Concordance des soldes' },
                      { label: 'Import validé', desc: 'Transactions prêtes' },
                    ].map((step, idx) => (
                      <div key={idx} data-testid={`ocr-step-${idx}`} className={`flex items-center gap-3 p-2 rounded-lg ${ocrStep >= idx + 1 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${ocrStep >= idx + 1 ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-600'}`}>
                          {ocrStep >= idx + 1 ? '✓' : idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-medium text-slate-700">{step.label}</div>
                          <div className="text-[11px] text-slate-500">{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SYNTHÈSE CLÔTURE */}
          {closureStatus && (
            <div className={`rounded-xl border p-4 ${closureBlocked ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {closureBlocked ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  <div>
                    <div className="text-sm font-semibold text-slate-800">
                      Synthèse de clôture — Période {period}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {closureBlocked
                        ? `Clôture bloquée : ${(reconKpis.unreconciled ?? kpis.unmatched_count ?? 0)} tx à traiter · ${(kpis.discrepancy_count ?? 0)} écart(s)`
                        : 'Prêt pour clôture — Toutes les transactions traitées'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="text-center"><div className="text-slate-500">À vérifier</div><div className="font-semibold text-amber-600">{reconKpis.to_review ?? kpis.needs_review_count ?? 0}</div></div>
                  <div className="text-center"><div className="text-slate-500">Écart solde</div><div className="font-semibold text-rose-600">{fmt(kpis.balance_discrepancy || 0)}</div></div>
                  <div className="text-center"><div className="text-slate-500">Non rapprochées</div><div className="font-semibold text-rose-600">{reconKpis.unreconciled ?? kpis.unmatched_count ?? 0}</div></div>
                </div>
              </div>
            </div>
          )}

          {/* TABLEAU TX */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <ScrollText className="h-4 w-4 text-slate-500" />
                Transactions {source === 'bank' ? '· Bank' : '· Wave'} <span className="text-slate-400 text-xs font-normal">({totalTx})</span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={generateReport}
                  className="text-xs inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                  data-testid="generate-report-btn"
                >
                  <FileText className="h-3.5 w-3.5" />
                  Générer rapport PDF
                </button>
              </div>
            </div>

            {/* Barre d'actions groupées (visible dès qu'une ligne est cochée) */}
            {selectedIds.size > 0 && (
              <div
                className="px-4 py-2 border-b border-slate-100 bg-blue-50/50 flex items-center justify-between flex-wrap gap-2"
                data-testid="bulk-actions-bar"
              >
                <div className="text-sm text-blue-800 font-medium">
                  {selectedIds.size} transaction{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}
                  <button
                    onClick={clearSelection}
                    className="ml-2 text-xs text-blue-600 hover:underline"
                    data-testid="bulk-clear-selection"
                  >
                    (tout désélectionner)
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={bulkBusy}
                    onClick={() => bulkAction('reconcile')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:bg-slate-300"
                    data-testid="bulk-reconcile-btn"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> Rapprocher
                  </button>
                  <button
                    disabled={bulkBusy}
                    onClick={() => bulkAction('ignore')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:bg-slate-300"
                    data-testid="bulk-ignore-btn"
                  >
                    <Ban className="h-3.5 w-3.5" /> Ignorer
                  </button>
                  <button
                    disabled={bulkBusy}
                    onClick={() => bulkAction('delete')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-600 text-white hover:bg-rose-700 disabled:bg-slate-300"
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Supprimer
                  </button>
                  <button
                    disabled={bulkBusy}
                    onClick={() => bulkAction('export')}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:bg-slate-100"
                    data-testid="bulk-export-btn"
                  >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100 text-xs uppercase text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAllPage}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        data-testid="select-all-checkbox"
                        title="Tout sélectionner sur cette page"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium">Date</th>
                    <th className="text-left px-3 py-2 font-medium">Libellé</th>
                    <th className="text-left px-3 py-2 font-medium">Réf</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-right px-3 py-2 font-medium">Débit</th>
                    <th className="text-right px-3 py-2 font-medium">Crédit</th>
                    <th className="text-center px-3 py-2 font-medium">Statut</th>
                    <th className="text-center px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-10 text-slate-400 text-sm">
                      {loading ? 'Chargement…' : 'Aucune transaction pour cette période'}
                    </td></tr>
                  )}
                  {transactions.map(tx => {
                    const isDebit = tx.direction === 'debit' || tx.amount < 0;
                    const dateStr = tx.operation_date || tx.transactionDate || tx.date;
                    const status = tx.reconciliation_status || tx.matchStatus || tx.status;
                    const isSelected = selectedTx?.id === tx.id;
                    const isChecked = selectedIds.has(tx.id);
                    return (
                      <tr
                        key={tx.id}
                        onClick={() => selectTx(tx)}
                        className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isSelected ? 'bg-blue-50/50' : ''} ${isChecked ? 'bg-blue-50/30' : ''}`}
                        data-testid={`tx-row-${tx.id}`}
                      >
                        <td className="px-3 py-2" onClick={(e) => { e.stopPropagation(); toggleSelect(tx.id); }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(tx.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            data-testid={`tx-checkbox-${tx.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{dateStr ? new Date(dateStr).toLocaleDateString('fr-FR') : '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-medium text-slate-800">{tx.label || tx.waveRef || tx.description || '—'}</div>
                          {(tx.counterparty || tx.recipientName) && <div className="text-[11px] text-slate-500">{tx.counterparty || tx.recipientName}</div>}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500 font-mono">{tx.reference || tx.waveRef || '—'}</td>
                        <td className="px-3 py-2 text-xs"><span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px]">{tx.type || (isDebit ? 'Débit' : 'Crédit')}</span></td>
                        <td className="px-3 py-2 text-xs text-right font-mono text-rose-600">{isDebit ? fmt(Math.abs(tx.amount)) : '—'}</td>
                        <td className="px-3 py-2 text-xs text-right font-mono text-emerald-600">{!isDebit ? fmt(Math.abs(tx.amount)) : '—'}</td>
                        <td className="px-3 py-2 text-center"><StatusBadge status={status} /></td>
                        <td className="px-3 py-2 text-center">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); selectTx(tx); }}
                              className="p-1 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-600"
                              title="Voir suggestions"
                              data-testid={`tx-view-suggestions-${tx.id}`}
                            >
                              <Link2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); ignoreTx(tx); }}
                              className="p-1 rounded hover:bg-zinc-100 text-slate-400 hover:text-zinc-700"
                              title="Ignorer (hors périmètre)"
                              data-testid={`tx-ignore-${tx.id}`}
                            >
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2 text-xs" data-testid="pagination-bar">
              <div className="flex items-center gap-2 text-slate-600">
                <span>Lignes par page :</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="border border-slate-300 rounded px-1.5 py-0.5 bg-white"
                  data-testid="page-size-select"
                >
                  {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="text-slate-500">
                  {totalTx === 0 ? '0' : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalTx)}`} sur {totalTx}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="p-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-prev"
                  aria-label="Page précédente"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-slate-700 font-medium">Page {page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  className="p-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pagination-next"
                  aria-label="Page suivante"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right : Détail tx + suggestions */}
        <div className="col-span-12 xl:col-span-3">
          <div className="sticky top-40 bg-white rounded-xl border border-slate-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">Détail transaction</div>
              {selectedTx && (
                <button onClick={() => { setSelectedTx(null); setCandidates([]); }} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {!selectedTx && (
              <div className="text-xs text-slate-400 text-center py-10">
                Cliquez sur une transaction pour voir les suggestions de rapprochement
              </div>
            )}

            {selectedTx && (
              <>
                <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-medium">{(selectedTx.operation_date || selectedTx.transactionDate) ? new Date(selectedTx.operation_date || selectedTx.transactionDate).toLocaleDateString('fr-FR') : '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Libellé</span><span className="font-medium truncate max-w-[150px]" title={selectedTx.label || selectedTx.waveRef}>{selectedTx.label || selectedTx.waveRef || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Référence</span><span className="font-mono">{selectedTx.reference || selectedTx.waveRef || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Montant</span><span className={`font-semibold ${(selectedTx.direction === 'debit' || selectedTx.amount < 0) ? 'text-rose-600' : 'text-emerald-600'}`}>{(selectedTx.direction === 'debit' || selectedTx.amount < 0) ? '-' : '+'}{fmt(Math.abs(selectedTx.amount))} XOF</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Statut</span><StatusBadge status={selectedTx.reconciliation_status || selectedTx.matchStatus} /></div>
                </div>

                <div className="border-t border-slate-100 pt-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Suggestions de rapprochement</div>
                  {candidatesLoading ? (
                    <div className="text-center py-4"><Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" /></div>
                  ) : candidates.length === 0 ? (
                    <div className="text-xs text-slate-400 text-center py-4">Aucune suggestion automatique</div>
                  ) : (
                    <div className="space-y-2">
                      {candidates.slice(0, 3).map((c, i) => {
                        const sourceLabels = {
                          contract_invoice: { icon: FileText, label: 'Facture contrat', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                          operation: { icon: Building2, label: 'Opération', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                          contract: { icon: FileText, label: 'Contrat', color: 'bg-violet-50 border-violet-200 text-violet-700' },
                        };
                        const sl = sourceLabels[c.source] || sourceLabels.operation;
                        const Icon = sl.icon;
                        return (
                          <div key={i} className={`rounded-lg border p-3 ${sl.color}`} data-testid={`suggestion-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1.5 text-xs font-medium">
                                <Icon className="h-3.5 w-3.5" />
                                {sl.label}
                              </div>
                              <span className="text-xs font-semibold">{Math.round((c.confidence || 0) * 100)}%</span>
                            </div>
                            <div className="text-xs font-medium text-slate-800 truncate" title={c.label}>{c.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{fmt(c.amount || 0)} XOF</div>
                            <button
                              onClick={() => matchTx(selectedTx, c)}
                              className="mt-2 w-full text-xs bg-blue-600 text-white py-1.5 rounded hover:bg-blue-700 font-medium"
                              data-testid={`match-btn-${i}`}
                            >
                              Rapprocher
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
